"use client";

import { PROJECT_NEARBY_QUERY } from "@/gql/project";
import { bengaluruBounds } from "@/info/bengaluru-gps";
import { useLazyQuery } from "@apollo/client";
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Interface representing the GPS coordinates.
 */
interface Gps {
  lat: number | null;
  lng: number | null;
  coordinates: [number, number];
}

/**
 * Interface representing the location details.
 */
interface Location {
  address: string;
  gps: Gps;
  city: string;
  pin: string;
}

/**
 * Interface representing the main data object.
 */
export interface NearbyProject {
  id: string;
  name: string;
  cover: string;
  localityName: string;
  location: Location;
}
interface UseNearbyProjectsParams {
  coordinates?: {
    lat: number;
    lng: number;
  };
  bounds?: typeof bengaluruBounds;
  maxDistance?: number;
  initialData?: NearbyProject[];
  initialError?: string | null;
  excludeId?: string;
}
interface UseNearbyProjectsReturn {
  data: NearbyProject[] | null;
  error: string | null;
  load: () => Promise<void>;
  onRefetch: () => void;
}
export function useNearbyProjects({
  coordinates,
  maxDistance,
  bounds
}: UseNearbyProjectsParams): UseNearbyProjectsReturn {
  const [data, setData] = useState<NearbyProject[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const lastCoordinatesRef = useRef<string>("");
  const [fetchProjects] = useLazyQuery(PROJECT_NEARBY_QUERY, {
    fetchPolicy: "cache-first"
  });
  const load = useCallback(async () => {
    setError(null);
    // TODO: Refactoring to use bounds strictly in future
    if (!coordinates && !bounds) return;
    try {
      const {
        data: result,
        error: apolloError
      } = await fetchProjects({
        variables: {
          filters: {
            getNearbyProjects: bounds ? {
              bounds,
              maxDistance
            } : {
              latitude: coordinates!.lat.toString(),
              longitude: coordinates!.lng.toString(),
              maxDistance
            }
          }
        }
      });
      if (apolloError) {
        setError(apolloError.message);
        return;
      }
      const newData = result?.projects?.getNearbyProjects;
      if (newData) {
        setData(prev => {
          // Create a Map to store unique items based on coordinates
          const uniqueMap = new Map<string, NearbyProject>();

          // Add existing data to the map
          if (prev) {
            prev.forEach(item => {
              const key = `${item.location.gps.coordinates[0]},${item.location.gps.coordinates[1]}`;
              uniqueMap.set(key, item);
            });
          }

          // Add new data to the map (this will overwrite duplicates)
          newData.forEach((item_0: NearbyProject) => {
            const key_0 = `${item_0.location.gps.coordinates[0]},${item_0.location.gps.coordinates[1]}`;
            uniqueMap.set(key_0, item_0);
          });

          // Convert Map values back to array
          return Array.from(uniqueMap.values());
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load more");
    } finally {}
  }, [bounds, coordinates, fetchProjects, maxDistance]);
  const onRefetch = useCallback(() => {
    setError(null);
    // setData(null);
    load();
  }, [load]);

  // Initial data fetch on component mount if no initial data is provided
  useEffect(() => {
    // Only fetch on initial mount if no initial data is provided
    if (!data) {
      load();
    }
  }, [data, load]); // Include dependencies to ensure proper fetching

  // Reset state when coordinates or type changes and automatically fetch data if no initial data
  useEffect(() => {
    if (!coordinates) return;
    const currentCoordinates = `${coordinates.lat},${coordinates.lng}`;
    if (lastCoordinatesRef.current !== currentCoordinates) {
      setData(null);
      setError(null);
      lastCoordinatesRef.current = currentCoordinates;

      // Automatically fetch data if no initial data is provided
      if (!data) {
        load();
      }
    }
  }, [coordinates, data, load]);
  return {
    data,
    error,
    load,
    onRefetch
  };
}